import { useGridFilter } from 'ag-grid-react';
import { ChangeEvent, useCallback, useId, useState } from 'react';
import { Button, Card, FormCheck, Stack } from 'react-bootstrap';

import type {
  AgGridAfterGuiAttachedCallback,
  AgGridCustomFilterProps,
  AgGridDoesFilterPassCallback,
} from '@customTypes/ag-grid';
import { ORDER_STATUS_CONFIG } from '@helpers/order';

export const StatusFilter: React.FC<AgGridCustomFilterProps> = props => {
  const { model, reactContainer, getValue, onModelChange } = props;

  const [closeFilter, setCloseFilter] = useState<(() => void) | undefined>();
  const [selectedOption, setSelectedOption] = useState<string | null>(model?.filter);
  const headerId = useId();

  const afterGuiAttached = useCallback<AgGridAfterGuiAttachedCallback>(params => {
    setCloseFilter(() => params?.hidePopup);
  }, []);

  // This is not used when doing server-side filtering, and is only configured as a fallback
  const doesFilterPass = useCallback<AgGridDoesFilterPassCallback>(
    params => model === getValue(params.node),
    [model, getValue],
  );

  useGridFilter({
    afterGuiAttached,
    doesFilterPass,
  });

  const onFilterApply = () => {
    onModelChange({
      filter: selectedOption,
      filterType: 'text',
      type: 'equals',
    });

    if (closeFilter) {
      closeFilter();
    }
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSelectedOption(event.target.value);
  };

  if (reactContainer) {
    const menuElement = reactContainer.closest('.ag-menu');
    menuElement?.removeAttribute('aria-label');
    menuElement?.setAttribute('aria-labelledby', headerId);
  }

  return (
    <Card>
      <Card.Header as='h3' className='h6'>
        Filter by Status
      </Card.Header>
      <Card.Body>
        <Stack className='gap-2'>
          <Stack>
            <FormCheck
              type='radio'
              name='status'
              label='All'
              id='filter-status-all'
              checked={!selectedOption}
              autoFocus={!selectedOption}
              onChange={() => setSelectedOption(null)}
            />

            {Object.entries(ORDER_STATUS_CONFIG).map(([status, config]) => (
              <FormCheck
                key={status}
                type='radio'
                name='status'
                label={config.label}
                id={`filter-status-${status}`}
                value={status}
                checked={selectedOption === status}
                autoFocus={selectedOption === status}
                onChange={handleChange}
              />
            ))}
          </Stack>

          <div className='d-flex justify-content-end border-top pt-3'>
            <Button size='sm' onClick={onFilterApply}>
              Apply
            </Button>
          </div>
        </Stack>
      </Card.Body>
    </Card>
  );
};
